import { useLocation, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import { useGetProjectMembershipQuery } from "../../app/accounts/memberships";
import { useGetNotesTreeQuery } from "../../app/services/notes";
import { useGetLibraryTreeQuery } from "../../app/services/library";
import {
  projectMainBoardCacheKey,
  useEnsureProjectMainBoardMutation,
} from "../../app/services/imageBoard";
import { isSpecialistPosition } from "../../utils/memberPositionUtils";
import { canShowNotes, canShowLibrary } from "./projectNavItems";

/**
 * パネルタブ（ノート / ライブラリ）とイメージボードの表示条件、イメージボードのトグル遷移。
 * 下部バーのノート / ライブラリ・useProjectShortcuts（⌥7/⌥8/⌥9 ショートカット）が
 * 共通で使う（クエリはキャッシュ共有）。
 *
 * イメージボードはもう一度開くと開く前の画面へ戻る（トグル）。「元の画面」は
 * 開くときに `location.state.imageBoardFrom`（クエリ文字列）へ記録して
 * 保持する（state が無い直リンク / リロード時は home）。
 */
export const useProjectPanelTabs = () => {
  const location = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: membershipData } = useGetProjectMembershipQuery(
    projectId ?? "",
    { skip: !projectId || !currentUserData },
  );
  const { data: notesTreeData } = useGetNotesTreeQuery(
    { project_id: projectId ?? "" },
    { skip: !projectId || !currentUserData },
  );
  const { data: libraryTreeData } = useGetLibraryTreeQuery(
    { project_id: projectId ?? "" },
    { skip: !projectId || !currentUserData },
  );

  const position = membershipData?.data?.position;
  const showImageBoard = !!position && !isSpecialistPosition(position);

  // メインのボードの ID は ProjectWrapper（旧 ?tab=imageboard の振り替え）と
  // 同じキャッシュキーで共有し、どこで解決しても「表示中か」を判定できるようにする
  const [ensureMainBoard, { data: mainBoardData }] =
    useEnsureProjectMainBoardMutation({
      fixedCacheKey: projectMainBoardCacheKey(projectId),
    });
  const mainBoardId = mainBoardData?.data.note_id;

  const currentTab = searchParams.get("tab") ?? "home";
  const isNotesActive = currentTab === "notes";
  const isLibraryActive = currentTab === "library";
  const isImageBoardActive =
    isNotesActive && !!mainBoardId && searchParams.get("note") === mainBoardId;

  const toggleImageBoard = async () => {
    // ProjectWrapper の `?tab=` 同期エフェクトがタブを切り替える。
    // 既存のタブ切替（handleTabChange）と同じく replace で遷移する。
    if (isImageBoardActive) {
      const stateFrom = (location.state as { imageBoardFrom?: string } | null)
        ?.imageBoardFrom;
      setSearchParams(new URLSearchParams(stateFrom ?? ""), { replace: true });
      return;
    }
    if (!projectId) return;
    try {
      const res = await ensureMainBoard({ project_id: projectId }).unwrap();
      setSearchParams(
        { tab: "notes", note: res.data.note_id },
        { replace: true, state: { imageBoardFrom: searchParams.toString() } },
      );
    } catch {
      toast.error("イメージボードを開けませんでした");
    }
  };

  return {
    projectId,
    showImageBoard,
    // ノート / ライブラリの入口判定は横断タスクボードのプロジェクト見出し
    // （useProjectNavItems）と共用する（projectNavItems.ts）。
    // ノートは notes タブ表示中はノートが 0 件になっても出し続け、
    // 表示中のタブが下部バーから消えないようにする。
    showNotes: canShowNotes(notesTreeData?.data) || isNotesActive,
    // ライブラリも同様に library タブ表示中は出し続ける。
    showLibrary: canShowLibrary(libraryTreeData?.data) || isLibraryActive,
    toggleImageBoard,
  };
};
